import { useMemo } from 'react';
import { formatCalendarDate } from '../utils/date-time';
import { useBookingStore } from '../store/useBookingStore';
import { useServiceQuery } from '../queries';

/**
 * Hook to get current service from store + query
 */
export function useService() {
	const currentServiceId = useBookingStore((state) => state.currentServiceId);
	return useServiceQuery(
		{ id: currentServiceId },
		{ enabled: currentServiceId > 0 }
	);
}

/**
 * Hook to compute if the service is a date range booking
 */
export function useIsDateRange(): boolean {
	const { data: service } = useService();
	return useMemo(() => {
		return Boolean(service?.blockSize && service.blockSize >= 1440);
	}, [service?.blockSize]);
}

/**
 * Hook to compute the booking time value string for the hidden input
 */
export function useBookingTimeValue(): string {
	const { data: service } = useService();
	const selectedDate = useBookingStore((state) => state.selectedDate);
	const selectedRange = useBookingStore((state) => state.selectedRange);
	const selectedTime = useBookingStore((state) => state.selectedTime);
	const selectedDuration = useBookingStore((state) => state.selectedDuration);

	return useMemo(() => {
		if (!service) {
			return '';
		}

		const isDateRange = service.blockSize >= 1440;

		if (isDateRange) {
			if (selectedRange?.from && selectedRange?.to) {
				const startDateTime = `${formatCalendarDate(selectedRange.from)} 00:00:00`;
				const endDateTime = `${formatCalendarDate(selectedRange.to)} 23:59:59`;
				return `${startDateTime}|${endDateTime}`;
			} else if (selectedDate) {
				const startDateTime = `${formatCalendarDate(selectedDate)} 00:00:00`;
				const endDateTime = `${formatCalendarDate(selectedDate)} 23:59:59`;
				return `${startDateTime}|${endDateTime}`;
			}
			return '';
		} else if (selectedDate && selectedTime) {
			const startDateTime = `${formatCalendarDate(selectedDate)} ${selectedTime}`;

			// If duration differs from block size, treat as range
			if (selectedDuration && selectedDuration !== service.blockSize) {
				const endDate = new Date(selectedDate);
				const [hours, minutes] = selectedTime.split(':');
				endDate.setHours(
					parseInt(hours, 10),
					parseInt(minutes, 10) + selectedDuration
				);
				const endDateTime = `${formatCalendarDate(endDate)} ${endDate.toTimeString().slice(0, 8)}`;
				return `${startDateTime}|${endDateTime}`;
			}
			return startDateTime;
		}
		return '';
	}, [service, selectedDate, selectedRange, selectedTime, selectedDuration]);
}

/**
 * Hook to compute the effective duration
 */
export function useEffectiveDuration(): number {
	const { data: service } = useService();
	const selectedDuration = useBookingStore((state) => state.selectedDuration);

	return useMemo(() => {
		if (selectedDuration > 0) {
			return selectedDuration;
		}
		return service?.minDuration ?? service?.blockSize ?? 0;
	}, [selectedDuration, service?.minDuration, service?.blockSize]);
}
